import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Coins } from 'lucide-react';
import { api, useGame } from '../context/GameContext';

// ──────────────────────────────────────────────────────────────────────────
// Wheel-of-Fortune style horizontal reel for the "Buy Random Painting" flow.
// Mechanics:
//   1. Pool of ~30 decorative templates is fetched once. Strip is the pool
//      repeated `COPY_COUNT` times so we can land deep into the middle.
//   2. On spin, we POST /cards/buy-random and overlay the actually purchased
//      card onto ONE specific tile (`targetStripKey`) — that single tile is
//      the only thing that ever changes content. The rest of the strip is
//      stable, so the CSS transition is glitch-free.
//   3. Idle drift: slow continuous left-to-right scroll while the player
//      decides. Cancelled when spin starts.
//   4. Spin animation: long ease-out (~4s) cubic-bezier landing exactly at
//      the centre of the target tile, followed by a brief settle "shake".
//   5. Haptic ticks fire on every tile crossing the centre line.
// ──────────────────────────────────────────────────────────────────────────

const TILE_W = 96;        // px — tile width
const TILE_H = 132;       // px — tile height (taller so portrait paintings fit fully)
const TILE_GAP = 10;      // px — gap between tiles
const STEP = TILE_W + TILE_GAP;
const MIN_STRIP_TILES = 18; // strip always spans at least this many tiles so it
                            // fills the viewport even when the pool is tiny.
                            // Reduced from 25 → 18 (2026-06-29): with idle-drift
                            // running every frame, each tile contributes a
                            // composited layer (border + previously a
                            // box-shadow). 18 still covers a 380px viewport
                            // ~2.5x over (more than enough for a 4-second
                            // spin to look long) while shaving ~28% of the
                            // composite work — directly addresses the
                            // micro-stutter players reported during idle
                            // scroll on mid-range Android phones.
const SPIN_MS = 4000;     // total spin duration
const IDLE_PX_PER_SEC = 55;
const TICK_THROTTLE_MS = 80; // tick handler runs ~12fps instead of 60fps —
                             // plenty for the centre-label update + haptic
                             // ticks, and avoids constant getBoundingClientRect
                             // reads (which force a layout reflow each call).

const RARITY_COLOR = {
  common: '#9CA3AF',
  uncommon: '#22C55E',
  rare: '#3B82F6',
  legendary: '#A855F7',
  masterpiece: '#F59E0B',
};

const haptic = (style = 'light') => {
  try {
    const tg = window.Telegram?.WebApp;
    if (tg?.HapticFeedback?.impactOccurred) tg.HapticFeedback.impactOccurred(style);
  } catch (_) { /* noop */ }
};

const cleanTitle = (s) => (s || '').trim().replace(/^["'«»]+|["'«»]+$/g, '');

// Build a strip that's guaranteed to span the viewport, sprinkled with
// decorative uncommon/rare tiles so the carousel feels alive (the player
// occasionally sees a "rare" painting fly past). Cadence:
//   * Common bulk: drawn from `pool` cyclically (the only rarity the
//     server actually drops on this player; winner-tile placement is
//     anchored to one of these positions).
//   * Uncommon cameo: every 3-6 tiles, picked at random from `extras.uncommons`.
//   * Rare cameo: every 20-30 tiles, picked at random from `extras.rares`.
// The randomised cadence (rangeRoll) keeps the pattern from looking
// scripted across spins.
const rangeRoll = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

const buildStrip = (pool, extras = {}) => {
  if (!pool.length) return { tiles: [], copies: 0 };
  const copies = Math.max(6, Math.ceil(MIN_STRIP_TILES / pool.length));
  const totalSlots = pool.length * copies;
  const uncommons = extras.uncommons || [];
  const rares = extras.rares || [];
  const legendaries = extras.legendaries || [];

  // Winner lands at a deterministic position (mirrors targetIndex math
  // in handleSpin). The animation is going to come to a stop exactly
  // on this tile and the server-confirmed purchased card will be
  // painted over it. The player will glance at the immediate
  // neighbours during the slow deceleration tail — if one of those
  // neighbours happens to be a decorative uncommon/rare cameo, the
  // strip looks like it was about to land on an uncommon and then
  // visibly switches to common right before settle. Players read
  // that as the game cheating them.
  //
  // Fix: forbid decorative cameos at idxInCopy values within a
  // 2-slot window around `targetPosInCopy`. Because decorations are
  // now ASSIGNED PER idxInCopy (see comment below), protecting an
  // idxInCopy protects that slot in every copy — including the
  // winner copy.
  const targetPosInCopy = Math.floor(pool.length / 2);
  const PROTECT_WINDOW = 2;
  const isIdxProtected = (idx) => Math.abs(idx - targetPosInCopy) <= PROTECT_WINDOW;

  // ─── Decoration pattern: identical across copies ──────────────────
  //
  // The idle-drift loop wraps `offsetRef.current` modulo one-copy-length
  // for seamless scrolling, so for the wrap to be visually invisible the
  // tile under the centre indicator immediately before the wrap must be
  // identical to the one immediately after. We therefore pick decoration
  // positions ONCE per idxInCopy ([0, pool.length)) and project the same
  // template into that idxInCopy in EVERY copy.
  //
  // Cadence chosen to roughly mirror the gacha odds (50% C / 30% U /
  // 15% R / 5% L) so the player visibly sees that all four rarities
  // are possible drops, not just commons + occasional uncommons.
  const legendaryIdxs = new Map(); // idxInCopy → legendary template
  const rareIdxs = new Map();      // idxInCopy → rare template
  const uncommonIdxs = new Map();  // idxInCopy → uncommon template

  // Legendary cameo: 1 per copy at a random un-protected slot. Higher
  // rarities take priority over lower ones so the more impressive
  // border colour wins any positional collision.
  if (legendaries.length) {
    const candidates = [];
    for (let i = 0; i < pool.length; i++) {
      if (!isIdxProtected(i)) candidates.push(i);
    }
    if (candidates.length) {
      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      legendaryIdxs.set(pick, legendaries[Math.floor(Math.random() * legendaries.length)]);
    }
  }

  // Rare cameos: every 5-7 idxInCopy → ~3 rares per pool of 20 (≈15%).
  let nextRare = rangeRoll(5, 7);
  while (nextRare < pool.length) {
    if (rares.length && !isIdxProtected(nextRare) && !legendaryIdxs.has(nextRare)) {
      rareIdxs.set(nextRare, rares[Math.floor(Math.random() * rares.length)]);
    }
    nextRare += rangeRoll(5, 7);
  }

  // Uncommon cameos: every 2-4 idxInCopy → ~6 uncommons per pool of 20 (≈30%).
  let nextUnc = rangeRoll(2, 4);
  while (nextUnc < pool.length) {
    if (
      uncommons.length
      && !rareIdxs.has(nextUnc)
      && !legendaryIdxs.has(nextUnc)
      && !isIdxProtected(nextUnc)
    ) {
      uncommonIdxs.set(nextUnc, uncommons[Math.floor(Math.random() * uncommons.length)]);
    }
    nextUnc += rangeRoll(2, 4);
  }

  const out = [];
  for (let pos = 0; pos < totalSlots; pos++) {
    const copy = Math.floor(pos / pool.length);
    const idxInCopy = pos % pool.length;
    let tile;
    if (legendaryIdxs.has(idxInCopy)) {
      tile = legendaryIdxs.get(idxInCopy);
    } else if (rareIdxs.has(idxInCopy)) {
      tile = rareIdxs.get(idxInCopy);
    } else if (uncommonIdxs.has(idxInCopy)) {
      tile = uncommonIdxs.get(idxInCopy);
    } else {
      tile = pool[idxInCopy];
    }
    // Keep the per-copy `${copy}-${idxInCopy}` _key format so the spin
    // handler's winner-tile lookup (`${targetCopy}-${targetPosInCopy}`)
    // continues to match a real strip tile.
    out.push({ ...tile, _key: `${copy}-${idxInCopy}` });
  }
  return { tiles: out, copies };
};

const RandomPaintingReel = ({ onClose, onPurchased, buyCost, tutorial = false, initialPool, onPoolFetched }) => {
  const { user, refreshUser, t } = useGame();
  // Initial pool may arrive either as the legacy bare array (commons only)
  // or as an object `{ templates, uncommons, rares, legendaries }`.
  // Normalise both to the new shape so the strip builder can sprinkle
  // all four rarity tiers as cameos.
  const initialNormalised = useMemo(() => {
    if (!initialPool) return null;
    if (Array.isArray(initialPool)) return { templates: initialPool, uncommons: [], rares: [], legendaries: [] };
    if (initialPool.templates) return initialPool;
    return null;
  }, [initialPool]);

  const [pool, setPool] = useState(initialNormalised?.templates || []);
  const [extras, setExtras] = useState({
    uncommons: initialNormalised?.uncommons || [],
    rares: initialNormalised?.rares || [],
    legendaries: initialNormalised?.legendaries || [],
  });
  const [loading, setLoading] = useState(!(initialNormalised?.templates?.length));
  const [spinning, setSpinning] = useState(false);
  // `purchasedCard` is set the moment the server confirms the purchase. The
  // strip uses this to render the winner image DURING the spin (otherwise
  // the tile would show the original pool template until the spin ended,
  // then visibly swap → looks like cheating).
  const [purchasedCard, setPurchasedCard] = useState(null);
  // `revealed` flips once the spin transition has finished — we use it to
  // gate the status text + Continue button (so they don't appear early).
  const [revealed, setRevealed] = useState(false);
  const [winnerKey, setWinnerKey] = useState(null); // strip _key the winner sits at
  const [errorText, setErrorText] = useState('');
  const [settle, setSettle] = useState(false);
  // Locks the centre rarity label to the purchased card during the final
  // deceleration window of the spin. Prevents the player from briefly
  // reading "uncommon" off the tile neighbouring the winner before the
  // strip settles to its actual common-rarity landing tile. Set to true
  // ~600ms before SPIN_MS elapses.
  const [spinLockCentre, setSpinLockCentre] = useState(false);
  const [pending, setPending] = useState(false);

  const stripRef = useRef(null);
  const offsetRef = useRef(0);
  const idleRafRef = useRef(null);
  const warmupRafRef = useRef(null);
  const tickRafRef = useRef(null);
  const lastTickIdxRef = useRef(null);
  const lastTickTimeRef = useRef(0);
  // Cached viewport width. Reading `parentElement.offsetWidth` every tick
  // (even throttled to 80ms) forces a layout reflow, which is one of the
  // sources of idle-drift micro-stutter. We seed it once on mount and
  // refresh via a ResizeObserver (orientation change, keyboard open, etc.)
  // — never inside the animation loop.
  const containerWRef = useRef(360);
  // Index in `strip` of the tile currently sitting on the centre line. Used
  // by the labels above/below the reel to show the painting's artist, title
  // and year in real time as the strip scrolls past.
  const [centreIndex, setCentreIndex] = useState(0);

  const enoughCoins = tutorial || (user?.coins || 0) >= buyCost;
  const stripReady = pool.length > 0;

  // ─── 1. Load decoration pool — only if parent didn't pre-fetch ─────────
  useEffect(() => {
    if (initialNormalised?.templates?.length) return;
    let cancelled = false;
    api.get('/cards/preview-pool').then(res => {
      if (cancelled) return;
      const items = res.data?.templates || [];
      const uncommons = res.data?.uncommons || [];
      const rares = res.data?.rares || [];
      const legendaries = res.data?.legendaries || [];
      setPool(items);
      setExtras({ uncommons, rares, legendaries });
      setLoading(false);
      try { onPoolFetched?.({ templates: items, uncommons, rares, legendaries }); } catch (_) { /* noop */ }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { tiles: strip, copies: stripCopies } = useMemo(
    () => (stripReady ? buildStrip(pool, extras) : { tiles: [], copies: 0 }),
    [pool, extras, stripReady]
  );

  // The painting whose tile is currently sitting on the centre line.
  // Priority order:
  //   1) After reveal → purchasedCard (truth).
  //   2) During the last ~600ms of the spin → purchasedCard, even if the
  //      strip hasn't fully settled yet. This is the critical window: the
  //      strip is moving very slowly here and the player's eye locks onto
  //      whatever's at centre. Showing the real rarity early prevents the
  //      "almost-an-uncommon, became-common" cheating perception bug.
  //   3) During idle / early spin → whichever pool template is currently
  //      under the centre line.
  const centreItem = useMemo(() => {
    if ((revealed || (spinning && purchasedCard && spinLockCentre)) && purchasedCard) {
      return {
        title: purchasedCard.title,
        artist: purchasedCard.artist,
        year: purchasedCard.year,
        rarity: purchasedCard.rarity,
      };
    }
    if (!strip.length) return null;
    const safeIdx = ((centreIndex % strip.length) + strip.length) % strip.length;
    const tile = strip[safeIdx];
    return tile ? { title: tile.title, artist: tile.artist, year: tile.year, rarity: tile.rarity } : null;
  }, [strip, centreIndex, revealed, purchasedCard, spinning, spinLockCentre]);

  // ─── 2. Apply transform helper ─────────────────────────────────────────
  // NOTE: we set translate3d here directly on `style.transform`, which
  // overrides the Tailwind `-translate-y-1/2` class on the strip. We must
  // re-apply the -50% Y translation manually or the strip will sit at the
  // viewport top instead of being vertically centred (causing tile bottoms
  // to overflow & be clipped by the viewport's overflow:hidden).
  const applyTransform = useCallback(() => {
    if (stripRef.current) {
      stripRef.current.style.transform = `translate3d(${-offsetRef.current}px, -50%, 0)`;
    }
  }, []);

  // ─── 3. Idle drift while user reads ────────────────────────────────────
  useEffect(() => {
    if (!stripReady || spinning) return;
    let last = performance.now();
    let paused = document.hidden; // start paused if tab hidden
    const oneCopyLen = pool.length * STEP;
    const tick = (now) => {
      if (paused) {
        // Skip motion entirely; just keep the RAF alive so visibility
        // resume picks up cleanly. We also reset `last` so the dt
        // doesn't accumulate a huge gap on resume.
        last = now;
        idleRafRef.current = requestAnimationFrame(tick);
        return;
      }
      const dt = (now - last) / 1000;
      last = now;
      offsetRef.current += IDLE_PX_PER_SEC * dt;
      // Wrap inside the first copy so visuals stay seamless
      if (offsetRef.current >= oneCopyLen) offsetRef.current -= oneCopyLen;
      applyTransform();
      idleRafRef.current = requestAnimationFrame(tick);
    };
    const onVisibility = () => {
      paused = document.hidden;
      // Reset the clock when resuming so the first frame after wake-up
      // doesn't apply a huge dt-burst (which would manifest as a visible
      // jump in scroll position).
      if (!paused) last = performance.now();
    };
    document.addEventListener('visibilitychange', onVisibility);
    idleRafRef.current = requestAnimationFrame(tick);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (idleRafRef.current) cancelAnimationFrame(idleRafRef.current);
      idleRafRef.current = null;
    };
  }, [stripReady, spinning, applyTransform, pool.length]);

  // Cache the viewport width once on mount and refresh on resize. Keeps
  // the per-frame tick loop free of layout-forcing reads.
  useEffect(() => {
    if (!stripReady) return;
    const viewport = stripRef.current?.parentElement;
    if (!viewport) return;
    const sync = () => {
      const w = viewport.offsetWidth;
      if (w) containerWRef.current = w;
    };
    sync();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(sync);
      ro.observe(viewport);
    } else {
      window.addEventListener('resize', sync);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', sync);
    };
  }, [stripReady]);

  // Unmount safety: cancel the warmup RAF if the user navigates away
  // mid-spin (avoids React-warns + stale transforms on remount).
  useEffect(() => () => {
    if (warmupRafRef.current) {
      cancelAnimationFrame(warmupRafRef.current);
      warmupRafRef.current = null;
    }
  }, []);

  // ─── 4. Haptic + visual ticks on every centre crossing ─────────────────
  // Throttled to ~12fps (TICK_THROTTLE_MS=80) instead of 60fps. The tick
  // handler is otherwise cheap, but reading getBoundingClientRect on every
  // frame forces a layout reflow — and that's what made phones overheat
  // after a few minutes with the reel open. 12fps is more than fast enough
  // for the centre-label text & haptic feedback to feel responsive.
  useEffect(() => {
    if (!stripReady) return;
    let lastTick = 0;
    let raf = null;
    const check = (now) => {
      if (now - lastTick >= TICK_THROTTLE_MS) {
        lastTick = now;
        // During an active CSS transition, the actual visual position is
        // mid-interpolation while offsetRef holds only the end target.
        // Read the rect ONLY while spinning so the centre label tracks the
        // spin accurately; during idle drift offsetRef is authoritative
        // and we can skip the reflow entirely.
        let visualOffset = offsetRef.current;
        if (spinning) {
          const viewport = stripRef.current?.parentElement;
          const stripRect = stripRef.current?.getBoundingClientRect();
          const viewRect = viewport?.getBoundingClientRect();
          if (stripRect && viewRect) {
            visualOffset = viewRect.left - stripRect.left;
          }
        }
        const containerW = containerWRef.current || 360;
        const centerOffset = visualOffset + containerW / 2;
        const idx = Math.floor(centerOffset / STEP);
        if (lastTickIdxRef.current !== idx) {
          if (lastTickIdxRef.current !== null) {
            const elapsed = now - lastTickTimeRef.current;
            if (elapsed > 70) {
              haptic('light');
              lastTickTimeRef.current = now;
            }
          }
          lastTickIdxRef.current = idx;
          setCentreIndex(idx);
        }
      }
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    tickRafRef.current = raf;
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [stripReady, spinning]);

  // ─── 5. Spin handler ───────────────────────────────────────────────────
  // Hybrid flow: kick off a "warmup" RAF spin the *moment* the player
  // taps Roll — no waiting on the network — and run the purchase API in
  // parallel. When the response lands we (a) place the real purchased
  // painting into the deterministic winner slot, (b) wait two RAFs so
  // React actually paints that image, then (c) hand the strip over to a
  // CSS deceleration transition that ends on the winner tile. The
  // winner image is already in the DOM before the deceleration starts,
  // so the swap-on-landing bug stays fixed; and because motion starts
  // on the first frame after tap, the player no longer perceives the
  // ~400-1000ms server round-trip as a frozen "lag".
  const handleSpin = async () => {
    if (pending || spinning || !enoughCoins || !stripReady) return;
    setErrorText('');
    setPending(true);
    haptic('medium');

    // 1) Stop idle drift NOW so the warmup RAF owns offsetRef.
    if (idleRafRef.current) {
      cancelAnimationFrame(idleRafRef.current);
      idleRafRef.current = null;
    }
    setSpinning(true);

    // 2) Warmup RAF: accelerate to a high spin velocity, wrap offsetRef
    //    modulo one pool-copy length so we never overshoot the strip's
    //    rendered width (copies are visually identical so the wrap is
    //    invisible). The target plateau is tuned to match the initial
    //    velocity of the deceleration cubic-bezier below — that way the
    //    handoff from JS-driven warmup to CSS-driven decel is seamless
    //    (no visible jerk).
    const oneCopyLen = pool.length * STEP;
    let warmupRunning = true;
    let velocity = 0;
    const MAX_VELOCITY = 38;   // px / frame ≈ 2280 px/s at 60fps
    const ACCEL = 1.6;
    const warmupTick = () => {
      if (!warmupRunning) return;
      velocity = Math.min(MAX_VELOCITY, velocity + ACCEL);
      offsetRef.current = (offsetRef.current + velocity) % oneCopyLen;
      applyTransform();
      warmupRafRef.current = requestAnimationFrame(warmupTick);
    };
    warmupRafRef.current = requestAnimationFrame(warmupTick);

    const stopWarmup = () => {
      warmupRunning = false;
      if (warmupRafRef.current) {
        cancelAnimationFrame(warmupRafRef.current);
        warmupRafRef.current = null;
      }
    };

    // 3) Fire the purchase. Strip is already visibly spinning, so any
    //    network latency here is now hidden inside the warmup.
    let purchased = null;
    try {
      const url = `/cards/buy-random${tutorial ? '?tutorial=true' : ''}`;
      const res = await api.post(url);
      purchased = res.data?.card || null;
    } catch (err) {
      stopWarmup();
      setErrorText(
        err.response?.data?.detail ||
        t('reel_purchase_failed')
      );
      setSpinning(false);
      setPending(false);
      return;
    }
    if (!purchased) {
      stopWarmup();
      setErrorText(t('reel_purchase_failed'));
      setSpinning(false);
      setPending(false);
      return;
    }

    // 4) Place the purchased card into the deterministic winner slot
    //    while the strip is still warmup-spinning. CRITICAL: the
    //    strip's decorative tiles use a versioned URL
    //    (`/api/images/X?v={image_version}`) returned from
    //    /cards/preview-pool, but the buy-random response returns
    //    only the unversioned `/api/images/X`. Browsers cache by
    //    URL — including the query string — so swapping the tile's
    //    src from versioned→unversioned was a cold cache miss every
    //    time, causing a visible "old image → new image" swap a
    //    few hundred ms into deceleration as the network round-trip
    //    completed. Two-part fix:
    //
    //    (a) If the purchased template is already visible somewhere
    //        in the strip pool / extras, reuse its versioned URL so
    //        the swap is a same-key cache hit (instant repaint).
    //    (b) AWAIT the image's decode/load before pushing the new
    //        state to React. The strip stays on the old image until
    //        the new bytes are ready, so React's commit and the
    //        repaint happen in the same frame.
    const targetCopy = Math.max(2, Math.floor(stripCopies * 0.66));
    const targetPosInCopy = Math.floor(pool.length / 2);
    const targetStripKey = `${targetCopy}-${targetPosInCopy}`;
    const targetIndex = targetCopy * pool.length + targetPosInCopy;
    const targetTemplateId = purchased.template_id || purchased.id;
    const stripPaintings = [
      ...pool,
      ...(extras.uncommons || []),
      ...(extras.rares || []),
      ...(extras.legendaries || []),
    ];
    const stripMatch = stripPaintings.find((t) => t && t.id === targetTemplateId);
    const purchasedImageUrl = (stripMatch && stripMatch.image_url)
      || purchased.image_url
      || `/api/images/${targetTemplateId}`;
    // Stash the resolved URL on `purchased` so the strip render uses
    // exactly the same string the browser already cached. This is what
    // makes the "winner tile" handoff a zero-flicker repaint.
    purchased._reel_image_url = purchasedImageUrl;
    await new Promise((resolve) => {
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      try {
        const pre = new Image();
        pre.onload = finish;
        pre.onerror = finish;
        pre.src = purchasedImageUrl;
        // Failsafe: never block the spin longer than 1.2s, even on a
        // dropped connection. The spin must always conclude.
        setTimeout(finish, 1200);
      } catch (_) {
        finish();
      }
    });
    setPurchasedCard(purchased);
    setWinnerKey(targetStripKey);

    // 5) Two RAFs so React commits the winner-tile image to the DOM
    //    BEFORE we hand off to the deceleration transition.
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));

    // 6) Hand off to CSS deceleration. Read offsetRef AFTER the RAFs so
    //    we know exactly where the warmup left us, then compute a
    //    target that lands on the winner tile. Because offsetRef is
    //    wrapped to [0, oneCopyLen) the target is always ahead.
    stopWarmup();

    const containerW = containerWRef.current || stripRef.current?.parentElement?.offsetWidth || 360;
    const jitter = Math.round((Math.random() * 16) - 8);
    const targetOffset = targetIndex * STEP - containerW / 2 + STEP / 2 + jitter;

    // Deceleration: shorter than the original 4s because we've already
    // been spinning during the API wait. ~2.4s decel from ~2300 px/s
    // → ~0 feels like a real roulette wheel braking.
    const DECEL_MS = 2400;

    requestAnimationFrame(() => {
      if (!stripRef.current) return;
      stripRef.current.style.transition = `transform ${DECEL_MS}ms cubic-bezier(0.18, 0.6, 0.18, 1)`;
      offsetRef.current = targetOffset;
      applyTransform();
    });

    // 7) Centre-rarity label lock just before landing, same as before.
    setTimeout(() => {
      setSpinLockCentre(true);
    }, Math.max(0, DECEL_MS - 600));

    // 8) Reveal on landing.
    setTimeout(() => {
      haptic('heavy');
      if (stripRef.current) {
        stripRef.current.style.transition = '';
      }
      setSettle(true);
      setTimeout(() => setSettle(false), 360);
      setRevealed(true);
      try { refreshUser?.(); } catch (_) { /* noop */ }
      try { onPurchased?.(purchased); } catch (_) { /* noop */ }
    }, DECEL_MS + 60);
  };

  // ─── 6. Lock body scroll while modal open ───────────────────────────────
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      data-testid="reel-modal"
      className="fixed inset-0 z-[80] bg-black/90 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget && !spinning) onClose?.(); }}
    >
      {/* Settle shake keyframes injected once via inline <style> */}
      <style>{`
        @keyframes reel-settle {
          0%   { transform: translate3d(var(--reel-tx, 0), 0, 0); }
          25%  { transform: translate3d(calc(var(--reel-tx, 0) + 5px), 0, 0); }
          55%  { transform: translate3d(calc(var(--reel-tx, 0) - 3px), 0, 0); }
          80%  { transform: translate3d(calc(var(--reel-tx, 0) + 1px), 0, 0); }
          100% { transform: translate3d(var(--reel-tx, 0), 0, 0); }
        }
        .reel-winner-pulse {
          animation: reel-pulse 1.6s ease-in-out infinite;
        }
        @keyframes reel-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 208, 122, 0.0), 0 0 16px rgba(245, 208, 122, 0.55); }
          50%      { box-shadow: 0 0 0 4px rgba(245, 208, 122, 0.2), 0 0 28px rgba(245, 208, 122, 0.85); }
        }
      `}</style>

      {/* Close button (top-right). Title removed by request. */}
      {!spinning && (
        <button
          data-testid="reel-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[#F5F5F7] z-10"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Reel viewport */}
      <div className="flex-1 flex flex-col justify-center px-2">
        {loading ? (
          <div data-testid="reel-loading" className="text-center text-[#A1A1AA] text-sm py-12">
            {t('reel_loading')}
          </div>
        ) : !stripReady ? (
          <div className="text-center text-[#A1A1AA] text-sm py-12">
            {t('reel_unavailable')}
          </div>
        ) : (
          <>
            {/* Labels above the reel — artist + title + year of the tile
                under the centre line. Updates live as the strip scrolls. */}
            <div data-testid="reel-centre-label" className="text-center px-4 mb-3 select-none">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37]/80 font-semibold truncate">
                {centreItem?.artist || '\u00A0'}
              </div>
              <div className="text-sm text-[#F5F5F7] font-serif italic mt-0.5 line-clamp-1">
                {centreItem?.title ? cleanTitle(centreItem.title) : '\u00A0'}
              </div>
              <div className="text-[11px] tracking-[0.2em] text-[#A1A1AA] mt-0.5 min-h-[14px]">
                {centreItem?.year ? String(centreItem.year) : '\u00A0'}
              </div>
            </div>
          <div className="relative w-full overflow-hidden" style={{ height: TILE_H + 24 }}>
            {/* Edge fade gradients */}
            <div className="absolute left-0 top-0 bottom-0 w-16 z-20 pointer-events-none bg-gradient-to-r from-black via-black/60 to-transparent" />
            <div className="absolute right-0 top-0 bottom-0 w-16 z-20 pointer-events-none bg-gradient-to-l from-black via-black/60 to-transparent" />

            {/* Centre indicator: top arrow + thin gold line */}
            <div className="absolute left-1/2 top-0 z-30 pointer-events-none -translate-x-1/2">
              <div
                style={{
                  width: 0, height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderTop: '9px solid #F5D07A',
                  filter: 'drop-shadow(0 0 4px rgba(245, 208, 122, 0.7))',
                }}
              />
            </div>
            <div
              className="absolute left-1/2 top-0 bottom-0 z-30 pointer-events-none"
              style={{
                width: 2,
                transform: 'translateX(-1px)',
                background: 'linear-gradient(180deg, transparent 0%, #D4AF37 25%, #F5D07A 50%, #D4AF37 75%, transparent 100%)',
                boxShadow: '0 0 8px rgba(212,175,55,0.6)',
              }}
            />
            <div className="absolute left-1/2 bottom-0 z-30 pointer-events-none -translate-x-1/2">
              <div
                style={{
                  width: 0, height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderBottom: '9px solid #F5D07A',
                  filter: 'drop-shadow(0 0 4px rgba(245, 208, 122, 0.7))',
                }}
              />
            </div>

            {/* Strip */}
            <div
              ref={stripRef}
              className={`absolute top-1/2 left-0 -translate-y-1/2 flex items-center ${settle ? 'reel-settle-shake' : ''}`}
              style={{ gap: `${TILE_GAP}px`, willChange: 'transform' }}
              data-testid="reel-strip"
            >
              {/* `winnerIdxInCopy` is the second half of `winnerKey`
                  (e.g. "3-15" → 15). Every copy of the strip has a tile
                  at this idx; rendering the purchased card across ALL
                  of them — not just the exact winner copy — eliminates
                  the "last-moment swap" players were reporting:
                  previously the strip cycled through `pool[idx]` for
                  copies 0..N-1 and only the targetCopy tile flipped
                  to the purchased painting, so the deceleration ended
                  in a visible image/rarity teleport. Sharing the same
                  display object across copies gives the player visual
                  continuity all the way to settle. The exact winner
                  copy still gets the reveal-pulse highlight, the
                  others just look the same. */}
              {(() => {
                const winnerIdxInCopy = (winnerKey && purchasedCard)
                  ? winnerKey.split('-').slice(-1)[0]
                  : null;
                const purchasedDisplay = purchasedCard ? {
                  id: purchasedCard.template_id || purchasedCard.id,
                  title: purchasedCard.title,
                  rarity: purchasedCard.rarity,
                  // Reuse the URL that was actually pre-loaded into
                  // the browser cache during handleSpin step 4 — it
                  // matches the strip's existing decorative tile URLs
                  // when the template was already on the reel, which
                  // makes the winner-tile handoff a same-key cache
                  // hit (zero flicker).
                  image_url: purchasedCard._reel_image_url
                    || purchasedCard.image_url
                    || `/api/images/${purchasedCard.template_id || purchasedCard.id}`,
                  image_focus_x: purchasedCard.image_focus_x,
                  image_focus_y: purchasedCard.image_focus_y,
                } : null;
                return strip.map((it) => {
                  const itIdxInCopy = it._key.split('-').slice(-1)[0];
                  const isPurchasedSlot = !!(purchasedDisplay && winnerIdxInCopy !== null && itIdxInCopy === winnerIdxInCopy);
                  const isExactWinner = !!(purchasedCard && winnerKey === it._key);
                  const display = isPurchasedSlot ? purchasedDisplay : it;
                  const color = RARITY_COLOR[display.rarity] || '#444';
                  // Pulse only the EXACT winner tile after the spin
                  // actually finishes (revealed), so duplicates across
                  // copies stay quiet.
                  const pulseNow = isExactWinner && revealed;
                  const focusX = display.image_focus_x ?? 50;
                  const focusY = display.image_focus_y ?? 50;
                  return (
                    <div
                      key={it._key}
                      className={`shrink-0 rounded-md overflow-hidden border ${pulseNow ? 'reel-winner-pulse' : ''}`}
                      style={{
                        width: TILE_W,
                        height: TILE_H,
                        borderColor: color + (pulseNow ? 'FF' : '88'),
                        borderWidth: pulseNow ? 2 : 1,
                        // Box-shadow ONLY on the winner tile after reveal.
                        // Previously every tile carried a 12px blurred
                        // box-shadow (`0 0 12px ${color}33`), which meant
                        // the GPU had to recomposite ~25 overlapping
                        // blur-layers every single frame of the idle
                        // drift. On mid-range Android that was the
                        // dominant source of the micro-stutters players
                        // saw on slow scroll. Dropping the shadows on
                        // non-winner tiles keeps the colour cue (border
                        // already does it) while eliminating ~95% of the
                        // composite cost. The pulse on the winner is
                        // unchanged.
                        boxShadow: pulseNow ? `0 0 18px ${color}AA` : 'none',
                      }}
                    >
                      <img
                        src={display.image_url}
                        alt={display.title || ''}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `${focusX}% ${focusY}%` }}
                        draggable={false}
                      />
                    </div>
                  );
                });
              })()}
            </div>
          </div>
            {/* Rarity label below reel — coloured to match the tile border
                so the player instantly clocks the rarity of whatever's
                under the centre line. */}
            <div className="text-center mt-2 text-[11px] tracking-[0.22em] uppercase font-semibold min-h-[14px]"
              style={{ color: RARITY_COLOR[centreItem?.rarity] || '#A1A1AA' }}
            >
              {centreItem?.rarity ? centreItem.rarity : '\u00A0'}
            </div>
          </>
        )}

        {/* Status text under reel — only visible after the spin reveals
            the winner. We intentionally hide the idle hint ("Hit Buy…")
            since it duplicates the CTA on the bottom button. */}
        <div data-testid="reel-status" className="text-center mt-3 text-[12px] text-[#A1A1AA] min-h-[18px] px-4">
          {revealed && purchasedCard
            ? t('reel_you_got').replace('{title}', cleanTitle(purchasedCard.title))
            : ''}
        </div>

        {/* Action button — placed inside the centered viewport block so it
            sits right under the year/status, instead of being glued to the
            screen bottom. The whole stack (label · reel · year · status ·
            button) is then vertically centred by the parent flex. */}
        <div className="px-4 mt-4">
          {errorText && (
            <p data-testid="reel-error" className="text-center text-xs text-[#EF4444] mb-2">{errorText}</p>
          )}
          {revealed && purchasedCard ? (
            <button
              data-testid="reel-done-btn"
              onClick={onClose}
              className="w-full py-3.5 rounded-xl font-bold text-base bg-[#22C55E] text-[#0C0C14] active:scale-[0.98] transition"
            >
              {t('reel_continue')}
            </button>
          ) : (
            <button
              data-testid="reel-buy-btn"
              onClick={handleSpin}
              disabled={!enoughCoins || pending || spinning || loading || !stripReady}
              className={`w-full py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition ${
                !enoughCoins || pending || spinning || loading || !stripReady
                  ? 'bg-[#D4AF37]/10 text-[#D4AF37]/40 border border-[#D4AF37]/20 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#D4AF37] to-[#F5D07A] text-[#0C0C14] border border-[#D4AF37]/60 hover:shadow-[0_0_18px_rgba(212,175,55,0.55)] active:scale-[0.98]'
              }`}
            >
              {pending || spinning ? (
                <span>{t('reel_spinning')}</span>
              ) : !enoughCoins ? (
                <>
                  <Coins className="w-4 h-4" />
                  <span>
                    {t('reel_need_more').replace('{n}', (buyCost - (user?.coins || 0)).toLocaleString())}
                  </span>
                </>
              ) : tutorial ? (
                <span>{t('reel_free_painting')}</span>
              ) : (
                <>
                  <span>{t('reel_roll_for')}</span>
                  <span className="font-mono">{buyCost.toLocaleString()}</span>
                  <Coins className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RandomPaintingReel;
